import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal, viewChild } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbNavChangeEvent, NgbNavModule, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ViewType } from '@ortho-next/nextray-core';
import { delay, finalize, first, forkJoin, map, Observable, of, switchMap, tap, throwError, timer } from 'rxjs';
import { WorkflowRunning } from '../../../nextray/Core/State';
import { AnatomicalSideEnum, BoneTypeEnum, BoneViewEnum, DeviceEnum, Image, ImageOrientationEnum, LoaderService, MountingParameters, ToastService } from '../../core';
import { EditToolResult, IImageCalibrationControl, IImageCategorizationControl, ImageCalibrationControl, ImageCalibrationForm, ImageCategorizationControl, ImageCategorizationForm, ImageItem, PlanForm, RingCalibrationInfo, RingCalibrationResult, setImgCategValidators } from '../../models';
import { AiService, AttachmentService, CanvasService, PlanFormService } from '../../services';
import { BaseComponent, ConfirmationComponent } from '../../shared';
import { EditToolsComponent } from '../edit-tools';
import { ImageCalibrationComponent } from '../image-calibration';
import { ImageEditComponent } from '../image-edit';
import { ImageOrientationComponent } from '../image-orientation';


/**
 * Component for x-ray image garage modal.
 */
@Component({
	selector: 'image-garage',
	templateUrl: './image-garage.component.html',
	styles: [`
		:host {
			height: 100%;
		}
	`],
	imports: [
		NgbTooltip,
		NgbNavModule,
		EditToolsComponent,
		ImageEditComponent,
		ImageOrientationComponent,
		ImageCalibrationComponent
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class ImageGarageComponent extends BaseComponent implements OnInit, OnDestroy {

	readonly canvasSrv = inject(CanvasService);
	private readonly aiSrv = inject(AiService);
	private readonly modalSrv = inject(NgbModal);
	private readonly toastSrv = inject(ToastService);
	private readonly loaderSrv = inject(LoaderService);
	private readonly activeModal = inject(NgbActiveModal);
	private readonly attachSrv = inject(AttachmentService);
	private readonly planFormSrv = inject(PlanFormService);


	edit = signal<boolean>(false);
	image = signal<ImageItem | null>(null);
	view = signal<BoneViewEnum | null>(null);

	isOrientationCorrect = signal<boolean | null>(null);
	isFulllegConfirmVisible = signal<boolean>(false);
	isFullleg = signal<boolean>(false);
	isValidationForced = signal<boolean>(false);
	isImgEdited = false;

	protected leftLabel = this.canvasSrv.leftLabel;
	protected rightLabel = this.canvasSrv.rightLabel;
	protected topLabel = this.canvasSrv.topLabel;
	protected bottomLabel = this.canvasSrv.bottomLabel;

	categorizationForm: FormGroup<IImageCategorizationControl> = new FormGroup(new ImageCategorizationControl());
	calibrationForm: FormGroup<IImageCalibrationControl> = new FormGroup(new ImageCalibrationControl());

	// calibration data
	circleDetectionPoints?: { x: number; y: number; }[] | null;
	ringCalibData: RingCalibrationInfo | null = null;
	calibrationCmp = viewChild(ImageCalibrationComponent);

	activeTab: 1 | 2 = 1;

	private get _calibValue(): ImageCalibrationForm {
		return this.calibrationForm.value as ImageCalibrationForm;
	}

	private get _categCtrl(): { [key in keyof ImageCategorizationForm]: AbstractControl } {
		return this.categorizationForm.controls as { [key in keyof ImageCategorizationForm]: AbstractControl };
	}

	get categValue(): ImageCategorizationForm {
		return this.categorizationForm.value as ImageCategorizationForm;
	}

	/**
	 * Check if reset analysis warning is visible.
	 */
	get isResetAnalysisWarning(): boolean {
		return !!this.image() && this.attachSrv.isOriginalImage(this.image()!.id, this.view()!) && !this.attachSrv.planImageAlreadyReset;
	}

	/**
	 * Check if rotate workflow is running.
	 */
	get isRotate(): boolean {
		return this.canvasSrv.runningWorkflow === WorkflowRunning.rotate;
	}

	ngOnInit(): void {
		this.canvasSrv.init();
		this.updateCanvasModel(this.image()!);
		setImgCategValidators(this.categorizationForm);
		this.planFormSrv.getForm().pipe(first()).subscribe(form => {
			this.updateForms(this.image()!, form.value as PlanForm);
			const checkupReadonly = this.planFormSrv.plan?.isCheckUp && !this.planFormSrv.plan?.preOp2PostOp;
			this.initRingCalibrationData(
				form.value as PlanForm,
				this.attachSrv.isRingToolDisabled(this.view()!),
				this.planFormSrv.checkRings,
				checkupReadonly
			);
			this.setCanvasLabels(form.value.boneType!, form.value.side!);
			this.isFulllegConfirmVisible.set(form.value.device === DeviceEnum.Fitbone && this.view() === BoneViewEnum.AP);
		});
	}

	private updateCanvasModel(image: ImageItem): void {
		if (this.edit()) {
			this.canvasSrv.setModel(image);
		} else {
			this.canvasSrv.setImage(image.file!);
		}
	}

	private updateForms(item: ImageItem, form: PlanForm): void {
		this.categorizationForm.reset();
		this.categorizationForm.reset(this.attachSrv.categorizationFormMapper(item as Image));
		this.categorizationForm.controls.boneType.setValue(form.boneType);
		this.categorizationForm.controls.boneView.setValue(this.view());
		this.categorizationForm.controls.boneSide.setValue(form.side);
		this.categorizationForm.markAsUntouched();
	}

	private initRingCalibrationData(
		form: PlanForm,
		preventRingCalibration: boolean,
		checkupRings: MountingParameters | null,
		checkupReadonly?: boolean
	): void {
		this.ringCalibData = form.postOperative ? {
			viewType: this.view() === BoneViewEnum.LAT ? ViewType.LT : ViewType.AP,
			boneType: form.boneType,
			refType: form.referenceType,
			side: form.side,
			isFoot: form.boneType === BoneTypeEnum.Forefoot || form.boneType === BoneTypeEnum.Hindfoot,
			proximalRingId: this.view() === BoneViewEnum.LAT ? this.attachSrv.apProximalRingId : this.attachSrv.ltProximalRingId,
			distalRingId: this.view() === BoneViewEnum.LAT ? this.attachSrv.apDistalRingId : this.attachSrv.ltDistalRingId,
			checkupProximalRingId: checkupRings?.ringProximalId ?? null,
			checkupDistalRingId: checkupRings?.ringDistalId ?? null,
			checkupReadonly: checkupReadonly ?? false,
			preventRingCalibration: preventRingCalibration
		} : null;
	}

	/**
	 * Set canvas labels.
	 */
	private setCanvasLabels(bone: BoneTypeEnum, side: AnatomicalSideEnum): void {
		this.canvasSrv.bottomLabel.set('');
		this.canvasSrv.topLabel.set('');
		this.canvasSrv.leftLabel.set('');
		this.canvasSrv.rightLabel.set('');
		const hasTopBottomLabels = bone == BoneTypeEnum.Forefoot || bone == BoneTypeEnum.Hindfoot;
		if (this.view() == BoneViewEnum.AP) {
			this.setOrientationLabels_AP(side, hasTopBottomLabels);
		} else {
			this.setOrientationLabels_LT(side, hasTopBottomLabels);
		}
	}

	private setOrientationLabels_AP(side: AnatomicalSideEnum, hasTopBottomLabels: boolean): void {
		if (side == AnatomicalSideEnum.Left) { // Left
			this.canvasSrv.leftLabel.set(this.labels().IMAGE_ORIENTATION_COMPONENT_BONE_ORIENTATION_MED);
			this.canvasSrv.rightLabel.set(this.labels().IMAGE_ORIENTATION_COMPONENT_BONE_ORIENTATION_LAT);
		} else { // Right
			this.canvasSrv.leftLabel.set(this.labels().IMAGE_ORIENTATION_COMPONENT_BONE_ORIENTATION_LAT);
			this.canvasSrv.rightLabel.set(this.labels().IMAGE_ORIENTATION_COMPONENT_BONE_ORIENTATION_MED);
		}

		if (hasTopBottomLabels) {
			this.canvasSrv.topLabel.set(this.labels().IMAGE_ORIENTATION_COMPONENT_BONE_ORIENTATION_POST);
			this.canvasSrv.bottomLabel.set(this.labels().IMAGE_ORIENTATION_COMPONENT_BONE_ORIENTATION_ANT);
		}
	}

	private setOrientationLabels_LT(side: AnatomicalSideEnum, hasTopBottomLabels: boolean): void {
		if (side == AnatomicalSideEnum.Left) { // Left
			this.canvasSrv.leftLabel.set(this.labels().IMAGE_ORIENTATION_COMPONENT_BONE_ORIENTATION_POST);
			this.canvasSrv.rightLabel.set(this.labels().IMAGE_ORIENTATION_COMPONENT_BONE_ORIENTATION_ANT);
		} else { // Right
			this.canvasSrv.leftLabel.set(this.labels().IMAGE_ORIENTATION_COMPONENT_BONE_ORIENTATION_ANT);
			this.canvasSrv.rightLabel.set(this.labels().IMAGE_ORIENTATION_COMPONENT_BONE_ORIENTATION_POST);
		}

		if (hasTopBottomLabels) {
			this.canvasSrv.topLabel.set(this.labels().IMAGE_ORIENTATION_COMPONENT_BONE_ORIENTATION_DORS);
			this.canvasSrv.bottomLabel.set(this.labels().IMAGE_ORIENTATION_COMPONENT_BONE_ORIENTATION_PLAN);
		}
	}

	/**
	 * Check if calibration tab is enabled.
	 */
	get isCalibrationEnabled(): boolean {
		return this.isOrientationValid && !this.isRotate;
	}

	/**
	 * Check if tab next button is enabled. 
	 */
	get isTabNextEnabled(): boolean {
		return (this.activeTab === 1 && this.isCalibrationEnabled) || (this.activeTab === 2 && this.isCalibrationValid);
	}

	/**
	 * Check if orientation tab is valid. 
	 */
	get isOrientationValid(): boolean {
		return this.isOrientationCorrect() !== null && (!this.isFulllegConfirmVisible() || this.isFullleg());
	}

	/**
	 * Check if calibration tab is valid. 
	 */
	get isCalibrationValid(): boolean {
		return this.calibrationForm.valid && !this.canvasSrv.isPointsRegistering;
	}

	/**
	 * Navigate to next tab.
	 */
	nextTab(ev?: NgbNavChangeEvent): void {
		const activeId = ev?.activeId ?? this.activeTab;
		const nextId = ev?.nextId ?? this.activeTab + 1;
		if (activeId === 1) {
			ev?.preventDefault();
			if (this.isTabNextEnabled) {
				this.activeTab = 2;
				this._toCalibrationTab();
			} else {
				this.validateTabs(true);
			}
		} else if (activeId === 2 && nextId === 3) {
			ev?.preventDefault();
			this.isTabNextEnabled ? this._toSave() : this.validateTabs();
		}
	}

	private validateTabs(onlyFirst = false): void {
		if (this.isRotate) {
			this.toastSrv.showError(this.labels().IMAGE_GARAGE_COMPONENT_WORKFLOW_ERROR);
		} else {
			this.isValidationForced.set(true);
			if (!onlyFirst) {
				const props = Object.keys(new ImageCalibrationControl()) as (keyof ImageCalibrationControl)[];
				for (const prop of props) {
					this.calibrationForm.controls[prop].markAsTouched();
					this.calibrationForm.controls[prop].updateValueAndValidity();
				}
			}
			this.toastSrv.showError(this.labels().IMAGE_GARAGE_COMPONENT_VALIDATION_ERROR);
		}
	}

	/**
	 * Navigate to previous tab.
	 */
	prev(): void {
		this.activeTab = 1;
	}

	/**
	 * Navigate to next tab.
	 */
	next(): void {
		this.nextTab();
	}

	private _toCalibrationTab(): void {
		this.handleCircleDetection();
	}

	private get isDataChanged(): boolean {
		return this.isOrientationCorrect() !== null || (this.isFulllegConfirmVisible() && this.isFullleg()) || this.isImgEdited;
	}

	/**
	 * Handle garage modal closure.
	 */
	closeModal(): void {
		if (this.isDataChanged) {
			this.openCloseModal();
		} else {
			this.activeModal.dismiss();
		}
	}

	private openCloseModal(): void {
		const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
		(modalRef.componentInstance as ConfirmationComponent).config = {
			title: this.labels().IMAGE_GARAGE_COMPONENT_CLOSE_MODAL_TITLE,
			message: this.labels().IMAGE_GARAGE_COMPONENT_CLOSE_MODAL_MESSAGE,
			cancelBtn: this.labels().IMAGE_GARAGE_COMPONENT_CLOSE_MODAL_NO,
			confirmBtn: this.labels().IMAGE_GARAGE_COMPONENT_CLOSE_MODAL_YES
		};
		modalRef.result.then(() => this.activeModal.dismiss()).catch(error => false);
	}

	/**
	* Fit to view the view.
	*/
	fitToView(): void {
		this.canvasSrv.dispatch('fitToView');
	}

	/**
	* Zoom in the view.
	*/
	zoomIn(): void {
		this.canvasSrv.dispatch('zoomIn');
	}

	/**
	* Zoom out the view.
	*/
	zoomOut(): void {
		this.canvasSrv.dispatch('zoomOut');
	}

	/**
	 * Reset image orientation.
	 */
	resetOrientation(): void {
		this.isOrientationCorrect.set(null);
		if (this.image()) {
			this.categorizationForm.controls.orientation.setValue(this.image()!.orientation);
		}
	}

	/**
	 * Update image orientation.
	 */
	updateOrientation(orientation: ImageOrientationEnum | null): void {
		this.categorizationForm.controls.orientation.setValue(orientation);
	}

	private handleCircleDetection(): void {
		if (!(globalThis as any).noCircleDetection && !(this.ringCalibData?.proximalRingId || this.ringCalibData?.distalRingId)) {
			this.loaderSrv.show(true);
			forkJoin([
				this.circleDetectionPoints === undefined
					? this.aiSrv.circleDetection(this.image()!.file!).pipe(tap(res => this.circleDetectionPoints = res?.coordinates ?? null))
					: of({}).pipe(delay(100))
			]).pipe(
				finalize(() => this.loaderSrv.hide())
			).subscribe(() => this.circleDetectionPoints && this.calibrationCmp()?.setCircleByPoints(this.circleDetectionPoints));
		}
	}

	/**
	 * Update scale factor.
	 */
	private updateScaleFactor(): Observable<boolean> {
		if (!this.canvasSrv.scaleFactor) {
			const value = this.calibrationForm.value.calibrationValue;
			this.canvasSrv.dispatch('calibrate', value!);
		}
		return timer(500).pipe(
			tap(() => {
				this._categCtrl.scaleFactor.setValue(this.canvasSrv.scaleFactor);
				this.updateCalibrationData();
				this.updateEditToolData();
			}),
			switchMap(() => this.categorizationForm.valid ? of(true) : throwError(() => new Error('Categorization form data not valid!')))
		);
	}

	private updateCalibrationData(): void {
		const isRingCalib = this._calibValue.calibrationTool === 'rings';
		this._categCtrl.isRingCalibration.setValue(isRingCalib);
		if (isRingCalib) {
			const ringCalib: RingCalibrationResult = {
				referenceType: this.ringCalibData?.refType ?? null,
				proxRingId: this._calibValue.proximalRing?.id ?? null,
				distalRingId: this._calibValue.distalRing?.id ?? null,
				ellipsePoints: this.canvasSrv.calibrationEllipsePoints ?? null,
				ringMatrix: this.canvasSrv.ringMatrix ?? null,
				angle: this.canvasSrv.angle ?? null,
				frontal: this.canvasSrv.frontal ?? null,
				axial: this.canvasSrv.axial ?? null
			};
			this._categCtrl.ringCalibration.setValue(ringCalib);
		} else {
			this._categCtrl.ringCalibration.reset(null);
		}
	}

	private updateEditToolData(): void {
		const data: EditToolResult = {
			brightness: this.canvasSrv.brightness ?? null,
			contrast: this.canvasSrv.contrast ?? null,
			rotation: this.canvasSrv.planeRotation ?? null
		};
		this._categCtrl.edit.setValue(data);
	}

	/**
	 * Handle image edit/upload.
	 */
	private _toSave(): void {
		if (this.edit()) {
			if (this.isResetAnalysisWarning) {
				this.openEditConfirmModal();
			} else {
				this.handleEdit();
			}
		} else {
			this.handleUpload();
		}
	}

	private openEditConfirmModal(): void {
		const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
		(modalRef.componentInstance as ConfirmationComponent).config = {
			title: this.labels().IMAGE_LOADER_COMPONENT_EDIT_MODAL_TITLE,
			message: this.labels().IMAGE_LOADER_COMPONENT_EDIT_MODAL_MESSAGE,
			cancelBtn: this.labels().IMAGE_LOADER_COMPONENT_EDIT_MODAL_NO,
			confirmBtn: this.labels().IMAGE_LOADER_COMPONENT_EDIT_MODAL_YES
		};
		modalRef.result.then(() => this.handleEdit()).catch(error => false);
	}

	private handleEdit(): void {
		this.loaderSrv.show(true);
		let item: ImageItem | null = null;
		this.updateScaleFactor().pipe(
			map(() => this.attachSrv.updateImageItem(this.image()!, this.categValue)),
			tap(res => item = res),
			switchMap(item => this.attachSrv.editImage(item)),
			finalize(() => this.loaderSrv.hide())
		).subscribe({
			next: () => {
				this.isResetAnalysisWarning && (this.attachSrv.planImageAlreadyReset = true);
				this.toastSrv.showSuccess(this.labels().IMAGE_GARAGE_COMPONENT_EDIT_SUCCESS);
				if (this.view() == BoneViewEnum.LAT) {
					this.attachSrv.setLtImageId(item!.id, false, item!.ringProximalId, item!.ringDistalId);
				} else if (this.view() == BoneViewEnum.AP) {
					this.attachSrv.setApImageId(item!.id, false, item!.ringProximalId, item!.ringDistalId);
				}
				this.activeModal.close();
			},
			error: () => this.toastSrv.showWarning(this.labels().IMAGE_GARAGE_COMPONENT_EDIT_ERROR)
		});
	}

	private handleUpload(): void {
		this.loaderSrv.show(true);
		this.updateScaleFactor().pipe(
			map(() => this.attachSrv.updateImageItem(this.image()!, this.categValue)),
			switchMap(item => this.attachSrv.uploadImage(item)),
			finalize(() => this.loaderSrv.hide())
		).subscribe({
			next: img => {
				this.toastSrv.showSuccess(this.labels().IMAGE_GARAGE_COMPONENT_UPLOAD_SUCCESS);
				if (this.view() == BoneViewEnum.LAT) {
					this.attachSrv.setLtImageId(img.id, false, img.ringProximalId, img.ringDistalId);
				} else if (this.view() == BoneViewEnum.AP) {
					this.attachSrv.setApImageId(img.id, false, img.ringProximalId, img.ringDistalId);
				}
				this.activeModal.close();
			},
			error: () => this.toastSrv.showWarning(this.labels().IMAGE_GARAGE_COMPONENT_UPLOAD_ERROR)
		});
	}

	private resetImageEdit(): void {
		if (this.canvasSrv.runningWorkflow === WorkflowRunning.rotate) {
			this.canvasSrv.dispatch('confirmRotate');
		}
		this.canvasSrv.dispatch('setBrightness', 0);
		this.canvasSrv.dispatch('setContrast', 0);
	}


	ngOnDestroy(): void {
		this.resetImageEdit();
	}

}
